// src/context/AuthContext.js
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { storeUserData } from "../utils/api";

const AuthContext = createContext({
  isAuthenticated: false,
  bootChecked: false,
  login: () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const INACTIVITY_LIMIT = 6 * 60 * 60 * 1000; // 6 hours
const WARNING_THRESHOLD = INACTIVITY_LIMIT - 60 * 1000; // show warning 1 minute before logout

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [bootChecked, setBootChecked] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const [showSessionWarning, setShowSessionWarning] = useState(false);

  // Track user activity without re-rendering. The timestamp is only read by the idle timer.
  useEffect(() => {
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };
    window.addEventListener("mousemove", updateActivity);
    window.addEventListener("keydown", updateActivity);
    window.addEventListener("click", updateActivity);
    window.addEventListener("pointerdown", updateActivity);
    return () => {
      window.removeEventListener("mousemove", updateActivity);
      window.removeEventListener("keydown", updateActivity);
      window.removeEventListener("click", updateActivity);
      window.removeEventListener("pointerdown", updateActivity);
    };
  }, []);

  // Verify stored token on boot
  useEffect(() => {
    const verify = async () => {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setBootChecked(true);
        return;
      }
try {
  const res = await fetch(`${API_BASE}/api/protected/`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.ok) {
    setIsAuthenticated(true);
    const data = await res.json();
    const stored = storeUserData(data);
    setUser({
      id: stored.id,
      username: stored.username,
      email: stored.email,
      first_name: stored.first_name,
      last_name: stored.last_name,
      full_name: `${stored.first_name} ${stored.last_name}`.trim(),
      groups: stored.groups,
      organization_id: stored.organization_id,
      organization_slug: stored.organization_slug,
      is_superuser: stored.is_superuser,
    });
  } else {
    setIsAuthenticated(false);
  }

  // ⭐ REQUIRED: BOOT COMPLETE
  setBootChecked(true);

} catch (err) {
  console.warn("Auth bootstrap check failed:", err);
  setIsAuthenticated(false);

  // ⭐ REQUIRED EVEN IF FAILS
  setBootChecked(true);
}

    };

    verify();
  }, []);

  const handleLogout = useCallback(() => {
    setShowSessionWarning(false);
    const currentPath = location.pathname + location.search;
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    setIsAuthenticated(false);
    navigate(`/login?next=${encodeURIComponent(currentPath)}`, { replace: true });
  }, [location, navigate]);

  // Auto logout after inactivity; show warning 1 minute before
  useEffect(() => {
    const interval = setInterval(() => {
      const inactiveTime = Date.now() - lastActivityRef.current;
      if (!isAuthenticated) return;
      if (inactiveTime >= INACTIVITY_LIMIT) {
        setShowSessionWarning(false);
        handleLogout();
      } else if (inactiveTime >= WARNING_THRESHOLD) {
        setShowSessionWarning(true);
      } else {
        setShowSessionWarning(false);
      }
    }, 30 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated, handleLogout]);

  const login = async ({ access, refresh }) => {
  localStorage.setItem("access_token", access);
  localStorage.setItem("refresh_token", refresh);
  setIsAuthenticated(true);
  lastActivityRef.current = Date.now();

  // ⭐ Fetch user info after login
  try {
    const res = await fetch(`${API_BASE}/api/protected/`, {
      headers: { Authorization: `Bearer ${access}` },
    });

    if (res.ok) {
      const data = await res.json();
      const stored = storeUserData(data);
      setUser({
        id: stored.id,
        username: stored.username,
        email: stored.email,
        first_name: stored.first_name,
        last_name: stored.last_name,
        full_name: `${stored.first_name} ${stored.last_name}`.trim(),
        groups: stored.groups,
        organization_id: stored.organization_id,
        organization_slug: stored.organization_slug,
        is_superuser: stored.is_superuser,
      });
    }
  } catch (err) {
    console.error("User profile load failed:", err);
  }
};


  const handleStaySignedIn = () => {
    lastActivityRef.current = Date.now();
    setShowSessionWarning(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        bootChecked,
        user,
        login,
        logout: handleLogout,
      }}
    >
      {children}
      <Dialog
        open={showSessionWarning}
        onClose={handleStaySignedIn}
        aria-labelledby="session-warning-title"
        aria-describedby="session-warning-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 320,
          },
        }}
      >
        <DialogTitle id="session-warning-title" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ScheduleIcon color="primary" />
          Session timeout soon
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="session-warning-description">
            You've been inactive for a while. You'll be signed out in about a minute unless you stay signed in.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleLogout} color="inherit" disableRipple>
            Sign out now
          </Button>
          <Button onClick={handleStaySignedIn} variant="contained" color="primary" autoFocus disableRipple>
            Stay signed in
          </Button>
        </DialogActions>
      </Dialog>
    </AuthContext.Provider>
  );
};
